// 🎛️ Sidebar layout defaults + per-space prefs (Nina 09-15, canvas-ruled:
// https://claude.ai/artifact/WzaL8qSeXX4kd9na5H3L5C).
//
// Non-competitive desktop three-column spaces can collapse the left "room"
// panel (livestream dock + locals) and the right ghost-chat column. The
// DEFAULT per panel is dynamic on the space's declared purpose — the build
// wizard's existing goal question (`space_goal`, served in every status
// payload; GOAL_PRESETS in spacesConfigMapper already stamps tool_flags from
// the same answer). Ruled map (09-15): story dens open fully quiet (both
// collapsed); tea rooms lead with the gossip lane (room collapsed, ghost
// open); hangout/watch/game open everything. Unknown/legacy (null goal) =
// both open — never guess a purpose for a space that predates the question.
//
// A viewer's first toggle in a space overrides that space's default there
// FOREVER (per space, per device — Nina's ruling over the global-pref
// proposal). Storage is localStorage, WYWA-style: every read/write is
// try/catch'd (private mode), and an unset key means "follow the preset",
// which is why prefs store explicit booleans and never materialize defaults.

// 📣 `live` (09-19): the ghost lane does not exist as a lane in a Live room
// (tool_flags.crowd_inline, utils/crowdInline.js — the crowd renders in the
// feed and GameStepPage forces the right column shut). `ghost: false` here is
// belt and braces so nothing ever resolves a lane open for that goal.
export const LAYOUT_DEFAULTS_BY_GOAL = {
  hangout: { room: true, ghost: true },
  live: { room: true, ghost: false },
  story: { room: false, ghost: false },
  watch: { room: true, ghost: true },
  game: { room: true, ghost: true },
  tea: { room: false, ghost: true },
};

const OPEN_OPEN = { room: true, ghost: true };

export function layoutDefaultsForGoal(spaceGoal) {
  return LAYOUT_DEFAULTS_BY_GOAL[spaceGoal] || OPEN_OPEN;
}

const storageKey = (gameId) => `space_layout_v1_${gameId}`;

/** Stored prefs for a space: {} or {room?: bool, ghost?: bool}. Never throws. */
export function loadLayoutPrefs(gameId) {
  try {
    const raw = localStorage.getItem(storageKey(gameId));
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch (e) { return {}; }
}

/** Persist one panel's explicit choice; returns the next prefs object. */
export function saveLayoutPref(gameId, panel, open) {
  const next = { ...loadLayoutPrefs(gameId), [panel]: !!open };
  try { localStorage.setItem(storageKey(gameId), JSON.stringify(next)); } catch (e) { /* private mode */ }
  return next;
}

/** Effective open-state: the viewer's explicit choice, else the goal default. */
export function resolvePanelOpen(pref, spaceGoal, panel) {
  if (typeof pref === 'boolean') return pref;
  return layoutDefaultsForGoal(spaceGoal)[panel];
}

// 📏 THE CAPPED CHAT COLUMN (Nina 09-17 pm, "maybe we shouldn't expand the
// middle horizontally"; the haze mockup's "Cap the chat column (1120px)"
// toggle, built 09-17 eve on her go). Before this, the center column took
// EVERY pixel a collapsed sidebar freed, so hiding the ghost lane on a wide
// monitor stretched chat lines to 1600px+. Now it stops at CHAT_COLUMN_MAX
// and centers: the freed space becomes wall on BOTH sides (the room's art,
// or the main room's wood). ONE rule, always on — it reads no room and no
// sidebar state, so it also holds with both sidebars open on very wide
// monitors. Scope for now = camera (house) spaces on desktop three-column
// layouts; everywhere else returns {} so the spread is byte-identical.
// The center column is a `flex: 1 1 0%` item: flex-grow fills up to the
// max-width first, THEN the leftover free space goes to the auto margins —
// which is exactly the centered-with-walls layout the mockup showed.
export const CHAT_COLUMN_MAX = 1120;

export function chatColumnCap(ctx) {
  if (!ctx || !ctx.roomCameraOn || ctx.isMobile || !ctx.isThreeColumnLayout) return {};
  return { maxWidth: CHAT_COLUMN_MAX, marginLeft: 'auto', marginRight: 'auto' };
}
